import { Input } from '@/components/ui/FormControls';
import PermissionGate from '@/components/common/PermissionGate';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Package, Search, ShoppingCart, Trash2, Wallet } from 'lucide-react';
import type { RecycleEntity, RecycleJob, RecycleRow } from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import {
  useRecycleBatch,
  useRecycleBin,
  useRecycleJob,
  useRecyclePreview,
  useRecycleRestore,
  usePendingRecycleJob,
  useCancelRecycleJob,
} from '@/hooks/useRecycleBin';
import { recycleErrorMessage } from '@/lib/recycleErrors';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import RecycleRecordCard, { recycleButton } from '@/components/recycle/RecycleRecordCard';
import RecycleCleanupDialog from '@/components/recycle/RecycleCleanupDialog';
import { toast } from 'sonner';
import PurchaseRecordsList from '@/pages/purchases/PurchaseRecordsList';
import { useOperations } from '@/hooks/useOperations';

const tabs = [
  { id: 'sales' as const, label: 'Deleted sales', icon: ShoppingCart },
  { id: 'payments' as const, label: 'Voided cash', icon: Wallet },
  { id: 'products' as const, label: 'Inactive products', icon: Package },
];
function savedJob(key: string): string | null {
  try {
    const id = localStorage.getItem(key);
    return id && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
  } catch {
    return null;
  }
}

export default function RecycleBinPage() {
  const { role, profile, organization, isLoading, can } = useAuth();
  const [purchases, setPurchases] = useState(false);
  const operations = useOperations();
  if (isLoading) return <LoadingState message="Loading your access…" />;
  if (!profile?.is_active || (role !== 'owner' && role !== 'admin'))
    return <Navigate to="/" replace />;
  return (
    <div className="space-y-4">
      {operations.data?.modules.purchases === 1 && can('purchases.view') && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Recycle bin categories">
          <button
            onClick={() => setPurchases(false)}
            aria-pressed={!purchases}
            className="min-h-11 rounded-xl border border-border px-4 text-sm"
          >
            Sales, cash & products
          </button>
          <button
            onClick={() => setPurchases(true)}
            aria-pressed={purchases}
            className="min-h-11 rounded-xl border border-border px-4 text-sm"
          >
            Deleted purchases
          </button>
        </div>
      )}
      {purchases && operations.data?.modules.purchases === 1 && can('purchases.view') ? (
        <PurchaseRecordsList bin />
      ) : (
        <RecycleBinContent
          key={`${organization?.id}:${profile.id}`}
          storageKey={`klothiq-recycle-job:${organization?.id}:${profile.id}`}
        />
      )}
    </div>
  );
}

function RecycleBinContent({ storageKey }: { storageKey: string }) {
  const [entity, setEntity] = useState<RecycleEntity>('sales');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [jobId, setJobId] = useState<string | null>(() => savedJob(storageKey));
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [operationError, setOperationError] = useState('');
  const [progress, setProgress] = useState<RecycleJob | null>(null);
  const stop = useRef(false);
  const mounted = useRef(true);
  const actionLock = useRef(false);
  const previewRequest = useRef<{ payload: string; key: string } | null>(null);
  const confirm = useConfirm();
  const list = useRecycleBin(entity, query, page);
  const preview = useRecyclePreview();
  const batch = useRecycleBatch();
  const cancelJob = useCancelRecycleJob();
  const restore = useRecycleRestore();
  const jobQuery = useRecycleJob(jobId);
  const pendingQuery = usePendingRecycleJob(!jobId);
  const job = progress?.job_id === jobId ? progress : (jobQuery.data ?? null);
  const busy = running || preparing || restore.isPending || cancelJob.isPending;
  const pendingJob = !!job?.confirmed && job.pending > 0;
  const unresolvedJob =
    (!!jobId && !job && (jobQuery.isLoading || jobQuery.isError)) ||
    (!jobId && (pendingQuery.isFetching || !!pendingQuery.data));
  const rows = list.data?.rows ?? [];
  const eligibleRows = rows.filter((row) => row.can_purge);
  const allPageSelected =
    eligibleRows.length > 0 && eligibleRows.every((row) => selected.has(row.id));

  useEffect(() => {
    if (!jobId && pendingQuery.data && !pendingQuery.isFetching) {
      setJobId(pendingQuery.data.job_id);
      setProgress(pendingQuery.data);
    }
  }, [jobId, pendingQuery.data, pendingQuery.isFetching]);
  useEffect(() => {
    if (jobQuery.data && !running && !preparing) setProgress(jobQuery.data);
  }, [jobQuery.data, running, preparing]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
      setSelected(new Set());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stop.current = true;
    };
  }, []);
  useEffect(() => {
    try {
      if (jobId) localStorage.setItem(storageKey, jobId);
      else localStorage.removeItem(storageKey);
    } catch {
      /* Persistence is optional; server outcomes remain authoritative. */
    }
  }, [jobId, storageKey]);
  useEffect(() => {
    if (list.data && page > 0 && page * 25 >= list.data.total)
      setPage(Math.max(0, Math.ceil(list.data.total / 25) - 1));
  }, [list.data, page]);

  function toggle(id: string) {
    setSelected((old) => {
      const next = new Set(old);
      if (next.has(id)) next.delete(id);
      else if (next.size < 500) next.add(id);
      else toast.error('Select up to 500 records, or use Delete all eligible.');
      return next;
    });
  }
  function changeTab(tab: RecycleEntity) {
    setEntity(tab);
    setPage(0);
    setSearch('');
    setQuery('');
    setSelected(new Set());
  }
  async function prepare(scope: 'all' | 'selected', ids?: string[]) {
    if (actionLock.current || pendingJob || unresolvedJob) return;
    actionLock.current = true;
    setPreparing(true);
    setOperationError('');
    const payload = { entity, scope, search: query, ...(ids ? { ids: [...ids].sort() } : {}) };
    const serialized = JSON.stringify(payload);
    if (previewRequest.current?.payload !== serialized)
      previewRequest.current = { payload: serialized, key: crypto.randomUUID() };
    try {
      const result = await preview.mutateAsync({
        ...payload,
        request_key: previewRequest.current.key,
      });
      if (!mounted.current) return;
      setJobId(result.job_id);
      setProgress(result);
      setOpen(true);
      previewRequest.current = null;
    } catch (err) {
      if (mounted.current) setOperationError(recycleErrorMessage(err));
    } finally {
      actionLock.current = false;
      if (mounted.current) setPreparing(false);
    }
  }
  async function execute(reason: string, confirmation: string) {
    if (actionLock.current || !job || !job.pending) return;
    actionLock.current = true;
    stop.current = false;
    setRunning(true);
    setOperationError('');
    try {
      let current = job;
      do {
        current = await batch.mutateAsync({ job_id: current.job_id, reason, confirmation });
        if (mounted.current) setProgress(current);
      } while (current.pending > 0 && !stop.current);
      if (mounted.current) {
        setSelected(new Set());
        if (!current.pending) {
          const message = `Cleanup finished: ${current.deleted} deleted, ${current.skipped} retained, ${current.failed} failed.`;
          if (current.failed) toast.warning(message);
          else toast.success(message);
        }
        await jobQuery.refetch();
      }
    } catch (err) {
      if (mounted.current) {
        setProgress(null);
        setOperationError(recycleErrorMessage(err));
        await jobQuery.refetch();
      }
    } finally {
      actionLock.current = false;
      if (mounted.current) setRunning(false);
    }
  }
  async function restoreRow(row: RecycleRow) {
    if (actionLock.current || pendingJob || unresolvedJob) return;
    actionLock.current = true;
    setPreparing(true);
    setOperationError('');
    try {
      const ok = await confirm({
        title: `Restore ${row.label}?`,
        description:
          entity === 'sales'
            ? 'Restore this sale, its available stock reservations and the payments reversed by its deletion. Previously voided payments stay voided.'
            : entity === 'products'
              ? 'Restore the product and its previously active variants. For older records without saved activation state, variants stay inactive and can be enabled in the product editor.'
              : 'Restore this transaction and apply its effect to the account balance.',
        confirmText: 'Restore',
        variant: 'primary',
      });
      if (!ok || !mounted.current) return;
      await restore.mutateAsync({ entity, row });
      toast.success(`${row.label} restored.`);
      setSelected((old) => {
        const next = new Set(old);
        next.delete(row.id);
        return next;
      });
    } catch (err) {
      if (mounted.current) setOperationError(recycleErrorMessage(err));
    } finally {
      actionLock.current = false;
      if (mounted.current) setPreparing(false);
    }
  }
  function dismiss() {
    setJobId(null);
    setProgress(null);
  }
  async function cancelRemaining() {
    if (!job || actionLock.current) return;
    actionLock.current = true;
    setPreparing(true);
    try {
      const result = await cancelJob.mutateAsync(job.job_id);
      setProgress(result);
      setOperationError('');
      toast.info('Remaining cleanup cancelled. Completed deletions are unchanged.');
    } catch (error) {
      setOperationError(recycleErrorMessage(error));
    } finally {
      actionLock.current = false;
      setPreparing(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Recycle Bin"
        description="Restore removed records or permanently delete eligible items. Financial and product history stays protected."
      />
      {jobId && (
        <div className="flex flex-col gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 text-sm">
            <p className="font-semibold">
              {jobQuery.isError
                ? 'Cleanup status needs attention'
                : running
                  ? 'Cleanup is running'
                  : pendingJob
                    ? 'Cleanup is paused'
                    : job?.confirmed
                      ? 'Last cleanup result'
                      : 'Saved cleanup preview'}
            </p>
            <p className="mt-1 text-muted-foreground">
              {job
                ? `${job.deleted} deleted · ${job.skipped} retained · ${job.failed} failed · ${job.pending} pending`
                : 'Check the recorded result before starting another cleanup.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className={`${recycleButton} border-border bg-background`}
              onClick={() => {
                setOpen(true);
                setOperationError('');
                void jobQuery.refetch();
              }}
              disabled={busy}
            >
              View {pendingJob ? 'and resume' : 'result'}
            </button>
            {!pendingJob && !busy && (
              <button className={`${recycleButton} border-transparent`} onClick={dismiss}>
                Dismiss
              </button>
            )}
            {jobQuery.isError && (
              <button
                className={`${recycleButton} border-border`}
                disabled={busy}
                onClick={() => void jobQuery.refetch()}
              >
                Retry status
              </button>
            )}
          </div>
        </div>
      )}
      <div className="overflow-x-auto border-b border-border">
        <div role="tablist" aria-label="Recycle bin categories" className="flex min-w-max gap-2">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              id={`recycle-tab-${id}`}
              type="button"
              role="tab"
              aria-selected={entity === id}
              aria-controls="recycle-panel"
              onKeyDown={(event) => {
                let index = tabs.findIndex((tab) => tab.id === entity);
                if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
                else if (event.key === 'ArrowLeft') index = (index + tabs.length - 1) % tabs.length;
                else if (event.key === 'Home') index = 0;
                else if (event.key === 'End') index = tabs.length - 1;
                else return;
                event.preventDefault();
                const target = tabs[index];
                if (!target) return;
                changeTab(target.id);
                document.getElementById('recycle-tab-' + target.id)?.focus();
              }}
              tabIndex={entity === id ? 0 : -1}
              disabled={busy}
              onClick={() => changeTab(id)}
              className={`flex min-h-11 items-center gap-2 border-b-2 px-3 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${entity === id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
            >
              <Icon className="h-4 w-4" />
              {label}
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {list.isLoading ? '…' : (list.data?.counts[id] ?? '—')}
              </span>
            </button>
          ))}
        </div>
      </div>
      <section
        id="recycle-panel"
        role="tabpanel"
        aria-labelledby={`recycle-tab-${entity}`}
        className="space-y-4"
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <label className="sr-only" htmlFor="recycle-search">
              Search recycle bin records
            </label>
            <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="recycle-search"
              type="search"
              maxLength={120}
              value={search}
              disabled={busy}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={
                entity === 'sales'
                  ? 'Sale number or customer…'
                  : entity === 'payments'
                    ? 'Description, account, type or date…'
                    : 'Product name or code…'
              }
              className="min-h-11 w-full rounded-lg border border-border bg-card pl-10 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              helpLabel="Search recycle bin records"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <PermissionGate permission="recycle.purge">
              <button
                className={`${recycleButton} border-destructive/25 text-destructive hover:bg-destructive/10`}
                disabled={
                  busy ||
                  pendingJob ||
                  unresolvedJob ||
                  !selected.size ||
                  list.isFetching ||
                  search.trim() !== query
                }
                onClick={() => void prepare('selected', [...selected])}
              >
                <Trash2 className="h-4 w-4" />
                Delete selected ({selected.size})
              </button>
            </PermissionGate>
            <PermissionGate permission="recycle.purge">
              <button
                className={`${recycleButton} border-destructive/25 bg-destructive/10 text-destructive hover:bg-destructive/20`}
                disabled={
                  busy ||
                  pendingJob ||
                  unresolvedJob ||
                  !list.data?.total ||
                  list.isFetching ||
                  search.trim() !== query
                }
                onClick={() => void prepare('all')}
              >
                <Trash2 className="h-4 w-4" />
                Delete all eligible
              </button>
            </PermissionGate>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Bulk deletion applies to this category{query ? ` matching “${query}”` : ''}, across all
          pages. Protected records remain in the bin. Every deletion requires a preview and
          confirmation.
        </p>
        {operationError && !open && (
          <p
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
          >
            {operationError}
          </p>
        )}
        {list.isLoading ? (
          <LoadingState message="Loading recycled records…" />
        ) : list.error ? (
          <ErrorState
            message={recycleErrorMessage(list.error)}
            onRetry={() => void list.refetch()}
          />
        ) : !rows.length ? (
          <EmptyState
            icon={<Trash2 className="h-7 w-7" />}
            title={query ? 'No matching records' : 'This category is empty'}
            description={
              query
                ? 'Try another search or clear the filter.'
                : 'Removed records will appear here when available.'
            }
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <label className="flex min-h-11 items-center gap-3">
                <Input
                  type="checkbox"
                  checked={allPageSelected}
                  disabled={busy || pendingJob || unresolvedJob || !eligibleRows.length}
                  onChange={() =>
                    setSelected((old) => {
                      const next = new Set(old);
                      eligibleRows.forEach((row) => {
                        if (allPageSelected) next.delete(row.id);
                        else if (next.size < 500) next.add(row.id);
                      });
                      return next;
                    })
                  }
                  className="h-5 w-5 accent-primary"
                  helpLabel="Select eligible items on this page"
                />
                Select eligible items on this page
              </label>
              <span className="text-muted-foreground">
                {list.data?.total} matching records{list.isFetching ? ' · Refreshing…' : ''}
              </span>
            </div>
            <div className="space-y-3">
              {rows.map((row) => (
                <RecycleRecordCard
                  key={row.id}
                  row={row}
                  entity={entity}
                  selected={selected.has(row.id)}
                  disabled={busy || pendingJob || unresolvedJob}
                  onSelect={() => toggle(row.id)}
                  onRestore={() => void restoreRow(row)}
                  onDelete={() => void prepare('selected', [row.id])}
                  onViewSales={() => changeTab('sales')}
                />
              ))}
            </div>
            <div className="flex items-center justify-between gap-3">
              <button
                className={`${recycleButton} border-border`}
                disabled={busy || page === 0}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {Math.max(1, Math.ceil((list.data?.total ?? 0) / 25))}
              </span>
              <button
                className={`${recycleButton} border-border`}
                disabled={busy || (page + 1) * 25 >= (list.data?.total ?? 0)}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>
      <RecycleCleanupDialog
        open={open}
        job={job}
        running={running || preparing || cancelJob.isPending}
        error={operationError || (jobQuery.error ? recycleErrorMessage(jobQuery.error) : '')}
        onClose={() => {
          stop.current = true;
          setOpen(false);
        }}
        onExecute={(reason, confirmation) => void execute(reason, confirmation)}
        onCancel={() => void cancelRemaining()}
        onReview={() => {
          dismiss();
          setSelected(new Set());
        }}
      />
    </div>
  );
}
